import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TablePagination,
  TableCell,
  CircularProgress,
  TableBody,
  TableRow
} from '@mui/material';

import EnhancedTableHead from '../EnhancedTableHead/EnhancedTableHead';
import { serviceSessionStorage } from '../../utils/serviceSessionStorage';

export const EnhancedTable = ({
  rows,
  headCells,
  filter,
  setFilter,
  children,
  total,
  showPagination = true,
  fetching,
  heightRow,
  rowsPerPage= [10, 20, 50],
  hideOrderFromDOM = false,
}) => {
  const [selected, setSelected] = useState([]);

  const currentPage = window.location.pathname;

  filter.size = serviceSessionStorage.getPaginationRows(currentPage) || filter.size;

  const handleRequestSort = (event, property) => {
    const isCamelCase = !!filter.orderBy;
    const isAsc = filter[isCamelCase ? 'orderBy' : 'order_by'] === property && filter[isCamelCase ? 'orderType' : 'order_type'] === 'asc';

    setFilter({ ...filter, [isCamelCase ? 'orderBy' : 'order_by']: property, [isCamelCase ? 'orderType' : 'order_type']: isAsc ? 'desc' : 'asc' });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setFilter({ ...filter, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setFilter({ ...filter, size, page: 0 });
    serviceSessionStorage.setPaginationRows(currentPage, size);
  };

  const heightByRows = rows?.length * heightRow;

  const basePropsToClone = hideOrderFromDOM ? { page: filter.page } : {
    page: filter.page,
    order: filter.orderType,
    rowsPerPage: filter.size,
    orderBy: filter.orderBy,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        sx={{
          width: '100%',
          boxShadow: 'none'
        }}>
        <TableContainer>
          <Table position="relative" data-testid="EnhancedTable">
            <EnhancedTableHead
              numSelected={selected?.length}
              order={filter.orderType || filter.order_type}
              orderBy={filter.orderBy || filter.order_by}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows?.length}
              headCells={headCells}
            />
            {fetching ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} height={`${heightByRows}px`}>
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <CircularProgress data-testid="CircularProgress" />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              React.cloneElement(children, basePropsToClone)
            )}
          </Table>
        </TableContainer>

        {showPagination && (
          <TablePagination
            rowsPerPageOptions={rowsPerPage}
            component="div"
            count={total}
            rowsPerPage={filter.size}
            page={filter.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Строк на странице"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count !== -1 ? count : `больше чем ${to}`}`}
            data-testid="TablePagination"
          />
        )}
      </Paper>
    </Box>
  );
};

EnhancedTable.propTypes = {
  children: PropTypes.node.isRequired,
  rows: PropTypes.array.isRequired,
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  headCells: PropTypes.array.isRequired,
  total: PropTypes.number,
  showPagination: PropTypes.bool,
  fetching: PropTypes.bool,
  heightRow: PropTypes.number,
  rowsPerPage: PropTypes.array,
  hideOrderFromDOM: PropTypes.bool,
};

export default memo(EnhancedTable);
